import { MessageSquare, Calendar, AlertTriangle, CheckCircle, Clock, Plus, X, Edit, Trash2, Download } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { quejasApi, busesApi } from '../lib/api';
import BusSelect from '../components/BusSelect';

interface Queja {
  id: number;
  numero: string;
  bus_id?: number;
  bus_padrón?: string;
  pasajero_nombre: string;
  pasajero_contacto?: string;
  pasajero_email?: string;
  fecha_queja: string;
  descripcion: string;
  categoria: string;
  prioridad: 'baja' | 'media' | 'alta' | 'critica';
  estado: 'pendiente' | 'en_proceso' | 'resuelta' | 'cerrada';
  respuesta?: string;
  fecha_respuesta?: string;
  responsable?: string;
  created_at: string;
  updated_at: string;
}

interface Bus {
  id: number;
  padron: string;
  numero?: string;
  placa: string;
  marca: string;
  modelo: string;
  estado: string;
}

const QuejasCompleto = () => {
  const [quejas, setQuejas] = useState<Queja[]>([]);
  const [buses, setBuses] = useState<Bus[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [quejaSeleccionada, setQuejaSeleccionada] = useState<Queja | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');
  const [filtroPrioridad, setFiltroPrioridad] = useState<string>('todos');
  
  const [formData, setFormData] = useState({
    bus_padrón: '',
    pasajero_nombre: '',
    pasajero_contacto: '',
    pasajero_email: '',
    descripcion: '',
    categoria: 'general',
    prioridad: 'media' as 'baja' | 'media' | 'alta' | 'critica'
  });

  const [respuestaData, setRespuestaData] = useState({
    respuesta: '',
    responsable: '',
    estado: 'en_proceso' as 'pendiente' | 'en_proceso' | 'resuelta' | 'cerrada'
  });

  // Cargar quejas y buses
  useEffect(() => {
    cargarQuejas();
    cargarBuses();

    // Sincronización periódica (ya no hay suscripción realtime de Supabase)
    const intervalo = setInterval(cargarQuejas, 30000);
    return () => clearInterval(intervalo);
  }, []);

  const cargarQuejas = async () => {
    try {
      const data = await quejasApi.getAll();
      if (data) {
        setQuejas(data);
        console.log('✅ Quejas cargadas:', data.length);
      }
    } catch (error) {
      console.error('Error al cargar quejas:', error);
      toast.error('Error de conexión al cargar quejas');
    }
  };

  const cargarBuses = async () => {
    try {
      const data = await busesApi.getAll();
      setBuses((data || []).filter((b: any) => b.estado === 'activo'));
      console.log('🚌 Buses cargados para quejas:', data?.length);
    } catch (error) {
      console.error('Error cargando buses:', error);
    }
  };

  const agregarQueja = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.pasajero_nombre.trim() || !formData.descripcion.trim()) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    try {
      await quejasApi.create({
        bus_padrón: formData.bus_padrón || null,
        pasajero_nombre: formData.pasajero_nombre,
        pasajero_contacto: formData.pasajero_contacto || null,
        pasajero_email: formData.pasajero_email || null,
        descripcion: formData.descripcion,
        categoria: formData.categoria,
        prioridad: formData.prioridad,
        estado: 'pendiente'
      });
      await cargarQuejas();

      toast.success('Queja registrada exitosamente');
      setShowForm(false);
      setFormData({
        bus_padrón: '',
        pasajero_nombre: '',
        pasajero_contacto: '',
        pasajero_email: '',
        descripcion: '',
        categoria: 'general',
        prioridad: 'media'
      });
    } catch (error: any) {
      console.error('Error agregando queja:', error);
      toast.error('Error al registrar queja: ' + (error.message || 'Error desconocido'));
    }
  };

  const responderQueja = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!quejaSeleccionada || !respuestaData.respuesta.trim()) {
      toast.error('Por favor completa la respuesta');
      return;
    }

    try {
      await quejasApi.update(quejaSeleccionada.id, {
        respuesta: respuestaData.respuesta,
        responsable: respuestaData.responsable || null,
        estado: respuestaData.estado,
        fecha_respuesta: new Date().toISOString()
      });
      await cargarQuejas();

      toast.success('Respuesta registrada exitosamente');
      setShowResponseForm(false);
      setQuejaSeleccionada(null);
      setRespuestaData({
        respuesta: '',
        responsable: '',
        estado: 'en_proceso'
      });
    } catch (error: any) {
      console.error('Error respondiendo queja:', error);
      toast.error('Error al responder: ' + (error.message || 'Error desconocido'));
    }
  };

  const eliminarQueja = async (queja: Queja) => {
    if (!confirm(`¿Estás seguro de eliminar la queja ${queja.numero}?`)) return;

    try {
      await quejasApi.delete(queja.id);
      await cargarQuejas();

      toast.success('Queja eliminada exitosamente');
    } catch (error: any) {
      console.error('Error eliminando queja:', error);
      toast.error('Error al eliminar: ' + (error.message || 'Error desconocido'));
    }
  };

  const descargarPdfQueja = async (queja: Queja) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${API_URL}/api/reportes/queja/${queja.id}/pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!response.ok) throw new Error('No se pudo generar el PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Queja_${queja.numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error generando PDF de queja:', error);
      toast.error('Error al generar el PDF: ' + (error.message || 'Error desconocido'));
    }
  };

  // Filtrar quejas
  const quejasFiltradas = quejas.filter(queja => {
    const filtroEstadoValido = filtroEstado === 'todos' || queja.estado === filtroEstado;
    const filtroPrioridadValido = filtroPrioridad === 'todos' || queja.prioridad === filtroPrioridad;
    return filtroEstadoValido && filtroPrioridadValido;
  });

  const obtenerColorPrioridad = (prioridad: string) => {
    switch (prioridad) {
      case 'critica': return 'text-red-600 bg-red-100';
      case 'alta': return 'text-orange-600 bg-orange-100';
      case 'media': return 'text-yellow-600 bg-yellow-100';
      case 'baja': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'pendiente': return 'text-yellow-600 bg-yellow-100';
      case 'en_proceso': return 'text-blue-600 bg-blue-100';
      case 'resuelta': return 'text-green-600 bg-green-100';
      case 'cerrada': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Quejas</h1>
          <p className="text-gray-600">Administra las quejas de los pasajeros - Sincronizado con Supabase</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Nueva Queja</span>
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-3">
            <MessageSquare className="text-blue-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Total Quejas</p>
              <p className="text-2xl font-bold">{quejas.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-3">
            <Clock className="text-yellow-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Pendientes</p>
              <p className="text-2xl font-bold">
                {quejas.filter(q => q.estado === 'pendiente').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-orange-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">En Proceso</p>
              <p className="text-2xl font-bold">
                {quejas.filter(q => q.estado === 'en_proceso').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-3">
            <CheckCircle className="text-green-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Resueltas</p>
              <p className="text-2xl font-bold">
                {quejas.filter(q => q.estado === 'resuelta').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por Estado
            </label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="en_proceso">En Proceso</option>
              <option value="resuelta">Resuelta</option>
              <option value="cerrada">Cerrada</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por Prioridad
            </label>
            <select
              value={filtroPrioridad}
              onChange={(e) => setFiltroPrioridad(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Todas las prioridades</option>
              <option value="critica">Crítica</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Quejas */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Número
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pasajero
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bus
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quejasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-lg">No hay quejas registradas</p>
                    <p className="text-sm">Haz clic en "Nueva Queja" para comenzar</p>
                  </td>
                </tr>
              ) : (
                quejasFiltradas.map((queja) => (
                  <tr key={queja.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {queja.numero}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar size={16} className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {formatearFecha(queja.fecha_queja)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {queja.pasajero_nombre}
                        </div>
                        {queja.pasajero_contacto && (
                          <div className="text-sm text-gray-500">
                            {queja.pasajero_contacto}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {queja.bus_padrón || 'No especificado'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        <p className="truncate" title={queja.descripcion}>
                          {queja.descripcion}
                        </p>
                        <span className="text-xs text-gray-500 capitalize">
                          {queja.categoria === 'pieza_repuesto' ? 'Pieza / Repuesto' : queja.categoria}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorPrioridad(queja.prioridad)}`}>
                        {queja.prioridad.charAt(0).toUpperCase() + queja.prioridad.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorEstado(queja.estado)}`}>
                        {queja.estado.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setQuejaSeleccionada(queja);
                            setRespuestaData({
                              respuesta: queja.respuesta || '',
                              responsable: queja.responsable || '',
                              estado: queja.estado
                            });
                            setShowResponseForm(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Responder"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => eliminarQueja(queja)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                        {queja.respuesta && (
                          <button
                            onClick={() => descargarPdfQueja(queja)}
                            className="text-green-600 hover:text-green-900"
                            title="Descargar PDF de la queja resuelta"
                          >
                            <Download size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para nueva queja */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Nueva Queja</h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={agregarQueja} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nombre del Pasajero *
                </label>
                <input
                  type="text"
                  value={formData.pasajero_nombre}
                  onChange={(e) => setFormData({...formData, pasajero_nombre: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Teléfono de Contacto
                </label>
                <input
                  type="text"
                  value={formData.pasajero_contacto}
                  onChange={(e) => setFormData({...formData, pasajero_contacto: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.pasajero_email}
                  onChange={(e) => setFormData({...formData, pasajero_email: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Bus (Padrón)
                </label>
                <BusSelect buses={buses} value={formData.bus_padrón} onChange={(v) => setFormData({...formData, bus_padrón: v})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Categoría
                </label>
                <select
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="general">General</option>
                  <option value="servicio">Servicio</option>
                  <option value="comodidad">Comodidad</option>
                  <option value="seguridad">Seguridad</option>
                  <option value="limpieza">Limpieza</option>
                  <option value="puntualidad">Puntualidad</option>
                  <option value="personal">Personal</option>
                  <option value="pieza_repuesto">Pieza / Repuesto</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Prioridad
                </label>
                <select
                  value={formData.prioridad}
                  onChange={(e) => setFormData({...formData, prioridad: e.target.value as any})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="critica">Crítica</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Descripción *
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
                >
                  Registrar Queja
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para responder queja */}
      {showResponseForm && quejaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Responder Queja #{quejaSeleccionada.numero}
              </h3>
              <button
                onClick={() => setShowResponseForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Pasajero:</strong> {quejaSeleccionada.pasajero_nombre}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Descripción:</strong> {quejaSeleccionada.descripcion}
              </p>
            </div>

            <form onSubmit={responderQueja} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Estado
                </label>
                <select
                  value={respuestaData.estado}
                  onChange={(e) => setRespuestaData({...respuestaData, estado: e.target.value as any})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en_proceso">En Proceso</option>
                  <option value="resuelta">Resuelta</option>
                  <option value="cerrada">Cerrada</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Responsable
                </label>
                <input
                  type="text"
                  value={respuestaData.responsable}
                  onChange={(e) => setRespuestaData({...respuestaData, responsable: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Nombre del responsable"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Respuesta *
                </label>
                <textarea
                  value={respuestaData.respuesta}
                  onChange={(e) => setRespuestaData({...respuestaData, respuesta: e.target.value})}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
                >
                  Enviar Respuesta
                </button>
                <button
                  type="button"
                  onClick={() => setShowResponseForm(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuejasCompleto;